/**
 * Aska Services (concierge) — shared buyer-facing constants for web + mobile,
 * so the two shells can't drift on the label or the fee.
 *
 * FEE SOURCE OF TRUTH (#31): `CONCIERGE_FEE_PERCENT` on the server
 * (functions/src/shared/concierge.ts, env-overridable), served on
 * GET /api/health as `feePercent`. The values below are LIVE BINDINGS
 * seeded with the launch default (15) and re-pointed by
 * `hydrateAskaServicesFee()`, which main.tsx fires at boot. If the fetch
 * fails or is slow, the seed value renders — same behavior as before —
 * and the server still snapshots its own fee at place-time, so display
 * drift can never change what a buyer is charged.
 *
 * Copy nuance (Tej, 2026-07-26): buyer pays the fee ON TOP for the
 * concierge service — QC, insured shipping, buying without contacting the
 * supplier — so the commission line always pairs with the subcopy below.
 */
import { hydrateStripeKey } from './stripeKey'
import { hydrateSeatPlan, hydrateSeatLadder } from './seatPlan'
import { hydrateAskaPlusPlan } from './askaPlus'
import { hydrateSourcingPlan } from './sourcing'
import { FLAT_PRICING, hydrateFlatPricing } from './flatPricing'

export let ASKA_SERVICES_FEE_PERCENT = 15

/**
 * THE buyer-facing price multiplier (FLAT_PRICING_LIVE-aware). Percentage
 * model ⇒ 1 + fee% (browse shows the fee-inclusive price, exactly as today);
 * flat model ⇒ 1 — the displayed price IS the supplier's real price and the
 * flat fee appears at checkout as a line item ("a broker hides the spread; we
 * show it"). Every display multiplier derives from THIS function, so flipping
 * the server flag re-prices every browse surface at the next render.
 */
export function askaServicesMult(): number {
  return FLAT_PRICING.live ? 1 : 1 + ASKA_SERVICES_FEE_PERCENT / 100
}

/** Aska+ — the paid-membership lane. Members are charged this DISCOUNTED
 *  concierge fee (vs the standard ASKA_SERVICES_FEE_PERCENT) on the same
 *  QC + insured-shipping fulfillment. Fixed member rate (2026-08). */
export const ASKA_PLUS_FEE_PERCENT = 12

/** Aska+ is DISPLAY-ONLY until the join flow + 12%-at-checkout engine ship.
 *  OFF (the default, unset in prod) hides every Aska+ surface so buyers are
 *  never shown a membership they can't join. Flip VITE_ASKA_PLUS_LIVE=true
 *  once it's a real end-to-end feature on BOTH web + mobile. */
export const ASKA_PLUS_LIVE = import.meta.env.VITE_ASKA_PLUS_LIVE === 'true'

/** The concierge purchase CTA — one label across web + mobile. */
export const ASKA_SERVICES_CTA_LABEL = 'Buy with Aska Services'

/** Cart-surface disclosure line (web drawer + mobile cart screen). */
export let ASKA_SERVICES_COMMISSION_LINE =
  `Aska service fee · ${ASKA_SERVICES_FEE_PERCENT}%`

/** Pairs with the line above — explains what the added fee buys. */
export const ASKA_SERVICES_COMMISSION_SUBCOPY =
  'Added for quality-check, insured shipping, and buying without contacting the supplier.'

/**
 * Re-point the fee bindings from the server value and broadcast
 * `aska:fee-changed` so derived values (mobile ASKA_MULT) recompute.
 * ESM live bindings mean importers read the updated values on their next
 * render — no reload needed. No-ops on the (usual) case server === seed.
 */
export function hydrateAskaServicesFee(feePercent: number): void {
  if (!Number.isFinite(feePercent) || feePercent < 0 || feePercent > 100) return
  if (feePercent === ASKA_SERVICES_FEE_PERCENT) return
  ASKA_SERVICES_FEE_PERCENT = feePercent
  ASKA_SERVICES_COMMISSION_LINE = `Aska service fee · ${feePercent}%`
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event('aska:fee-changed'))
  }
}

/**
 * Boot-time hydration — fire-and-forget fetch of /api/health. Plain fetch
 * (not the api client): anonymous, no auth-restore wait, silent on failure.
 */
export function initAskaServicesFee(): void {
  if (typeof window === 'undefined') return
  void fetch('/api/health')
    .then((r) => (r.ok ? r.json() : null))
    .then((j: {
      feePercent?: number
      stripePublishableKey?: string | null
      seatPlan?: unknown
      askaPlusPlan?: unknown
      sourcingPlan?: unknown
      flatPricing?: unknown
      seatLadder?: unknown
    } | null) => {
      if (j && typeof j.feePercent === 'number') hydrateAskaServicesFee(j.feePercent)
      // Piggyback: the signup wizard's address autocomplete needs the
      // publishable Stripe key; one boot fetch serves both.
      if (j) hydrateStripeKey(j.stripePublishableKey)
      // …and the team-seat price + its arm flag (step 10). Same invariant as the
      // fee: the price is SERVER-sourced, never a hardcoded client mirror.
      if (j) hydrateSeatPlan(j.seatPlan)
      // …and the Aska+ membership price + its arm flag. Same invariant again.
      if (j) hydrateAskaPlusPlan(j.askaPlusPlan)
      // …and the sourcing fee lines + the SOURCING_ORDERS_LIVE arm flag. Same
      // invariant: fees are SERVER-sourced, and `live` gates the place CTA.
      if (j) hydrateSourcingPlan(j.sourcingPlan)
      // …and the FLAT pricing model (FLAT_PRICING_LIVE) — flips every browse
      // multiplier to 1 + swaps the checkout/membership copy when live.
      if (j) hydrateFlatPricing(j.flatPricing)
      // …and the tiered seat LADDER presentation (display only — purchasing
      // stays behind canPurchaseSeats exactly as before).
      if (j) hydrateSeatLadder(j.seatLadder)
    })
    .catch(() => {
      /* offline / backend down — seed value stands */
    })
}
